package com.shadowings.gero.presentation.screen

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.shadowings.gero.domain.model.ChangelogEntry
import com.shadowings.gero.domain.model.changelogEntries
import com.shadowings.gero.presentation.ui.AppTheme
import compose.icons.FeatherIcons
import compose.icons.feathericons.X
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.changelog_close
import gero.composeapp.generated.resources.changelog_full_title
import gero.composeapp.generated.resources.changelog_title
import org.jetbrains.compose.resources.stringResource

@Preview
@Composable
private fun FullChangelogDialogContentPreview() {
    AppTheme {
        FullChangelogDialogContent(entries = changelogEntries, onDismiss = {})
    }
}

/**
 * Nearly-fullscreen "browse all updates" screen, opened from Settings — unlike [ChangelogDialog]
 * (which only ever shows the current version once), this lists every [ChangelogEntry] in history.
 */
@Composable
fun FullChangelogDialog(
    onDismiss: () -> Unit,
    entries: List<ChangelogEntry> = changelogEntries,
) {
    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(usePlatformDefaultWidth = false)
    ) {
        FullChangelogDialogContent(entries = entries, onDismiss = onDismiss)
    }
}

@Composable
private fun FullChangelogDialogContent(
    entries: List<ChangelogEntry>,
    onDismiss: () -> Unit,
) {
    Surface(
        modifier = Modifier
            .fillMaxWidth(0.95f)
            .fillMaxHeight(0.9f)
            .semantics { testTag = "changelog_full_screen" },
        shape = RoundedCornerShape(24.dp),
        color = MaterialTheme.colorScheme.surface,
        tonalElevation = 6.dp
    ) {
        Column(modifier = Modifier.fillMaxSize().padding(16.dp)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = stringResource(Res.string.changelog_full_title),
                    style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold)
                )
                IconButton(
                    onClick = onDismiss,
                    modifier = Modifier.semantics { testTag = "changelog_full_btn_close" }
                ) {
                    Icon(
                        imageVector = FeatherIcons.X,
                        contentDescription = stringResource(Res.string.changelog_close)
                    )
                }
            }
            Spacer(Modifier.height(8.dp))
            HorizontalDivider()

            LazyColumn(
                modifier = Modifier.fillMaxSize(),
                contentPadding = PaddingValues(vertical = 12.dp)
            ) {
                entries.forEachIndexed { index, entry ->
                    item(key = "header_${entry.version}") {
                        Text(
                            text = stringResource(Res.string.changelog_title, entry.version),
                            style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                            modifier = Modifier.padding(
                                top = if (index == 0) 0.dp else 20.dp,
                                bottom = 8.dp
                            )
                        )
                    }
                    itemsIndexed(entry.items, key = { itemIndex, _ -> "${entry.version}_$itemIndex" }) { _, item ->
                        Row(
                            modifier = Modifier.fillMaxWidth().padding(vertical = 2.dp),
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            Text(
                                text = "•",
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.onSurface
                            )
                            Text(
                                text = stringResource(item),
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.onSurface
                            )
                        }
                    }
                    if (index < entries.lastIndex) {
                        item(key = "divider_${entry.version}") {
                            Spacer(Modifier.height(12.dp))
                            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.4f))
                        }
                    }
                }
            }
        }
    }
}
